import { FieldErrors } from 'common/components/core/FieldErrors'
import React, { useEffect, useState } from 'react'
import { PublicPageFieldSlugEnum } from 'src/public-pages/enums/public-page-field-slug-enum'
import { useLoginSubmit } from 'src/public-pages/hooks/use-login-submit'
import { WebsitePublicLogo } from 'src/public-pages/shared/components/layout/website-public-logo'
import { PublicPageButton } from 'src/public-pages/shared/components/public-page-button'
import { PublicPageField } from 'src/public-pages/shared/components/public-page-field'
import { PublicPageHeading } from 'src/public-pages/shared/components/public-page-heading'
import { PublicPageLink } from 'src/public-pages/shared/components/public-page-link'
import { PublicPageText } from 'src/public-pages/shared/components/public-page-text'
import CommonCheckbox from 'src/shared/components/checkbox'
import { WebsitePublicPageType } from 'src/shared/enums/website-public-page-form-type'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import {
  WebsiteButtonsInterface,
  WebsiteFormFieldsSettings,
  WebsiteThemeInterface,
  WebsiteTypographySettingsStylesInterface,
} from 'src/shared/types/website-settings-types'
import { isCustomDomain } from 'src/shared/utils/is-custom-domain'
import DividerWithText from '../shared/components/divider-with-text'
import { PublicPageGoogleAuthButton } from '../shared/components/public-page-google-auth.button'

export interface LoginContentProps {
  logoUrl?: string
  isCustomDomain?: boolean
  heading?: WebsiteTypographySettingsStylesInterface
  paragraph?: WebsiteTypographySettingsStylesInterface
  theme?: WebsiteThemeInterface
  formFields?: Partial<WebsiteFormFieldsSettings>
  button?: Partial<WebsiteButtonsInterface>
  linkButton?: Partial<WebsiteButtonsInterface>
}

export const LoginContent: React.FC<LoginContentProps> = ({
  logoUrl,
  isCustomDomain: isCustomDomainProp,
  heading,
  theme,
  formFields,
  paragraph,
  button,
  linkButton,
}) => {
  const { t } = useTypedTranslation()
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])
  const {
    email,
    setEmail,
    password,
    setPassword,
    rememberMe,
    setRememberMe,
    handleSubmit,
    loading,
    errors,
  } = useLoginSubmit()
  return (
    <div>
      <WebsitePublicLogo
        logoUrl={logoUrl}
        isCustomDomain={isCustomDomainProp}
        alt="Logo"
      />
      <PublicPageHeading
        heading={heading}
        theme={theme}
        style={{ marginBottom: '40px' }}
      >
        {t('website_public_page.login.text')}
      </PublicPageHeading>
      <div style={{ marginBottom: '16px' }}>
        <div style={{ marginBottom: '16px' }}>
          <PublicPageText
            paragraph={paragraph}
            theme={theme}
            style={{ marginBottom: '10px' }}
          >
            {t('website_public_page.email.label')}
          </PublicPageText>

          <PublicPageField
            type={PublicPageFieldSlugEnum.Email}
            name={PublicPageFieldSlugEnum.Email}
            formFields={formFields}
            theme={theme}
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
          <FieldErrors
            errors={errors?.[PublicPageFieldSlugEnum.Email]}
            fontFamily={paragraph?.fontFamily}
          />
        </div>
        <div>
          <PublicPageText
            paragraph={paragraph}
            theme={theme}
            style={{ marginBottom: '10px' }}
          >
            {t('website_public_page.login.password.label')}
          </PublicPageText>
          <PublicPageField
            type={PublicPageFieldSlugEnum.Password}
            name={PublicPageFieldSlugEnum.Password}
            formFields={formFields}
            theme={theme}
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
          <FieldErrors
            errors={errors?.[PublicPageFieldSlugEnum.Password]}
            fontFamily={paragraph?.fontFamily}
          />
          <FieldErrors
            errors={errors?.root}
            fontFamily={paragraph?.fontFamily}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '36px',
              marginTop: '12px',
            }}
          >
            <CommonCheckbox
              uiProps={{}}
              checked={rememberMe}
              onChangeHandler={() => {
                setRememberMe(prev => !prev)
              }}
            >
              <PublicPageText
                paragraph={paragraph}
                theme={theme}
                style={{ margin: '0 0 0 8px' }}
              >
                {t('website_public_page.login.remember_me_text')}
              </PublicPageText>
            </CommonCheckbox>

            <PublicPageLink
              linkButton={linkButton}
              paragraph={paragraph}
              theme={theme}
              href={`/${WebsitePublicPageType.ForgotPassword}`}
              textAlign="right"
            >
              {t('website_public_page.login.forgot_password_button_text')}
            </PublicPageLink>
          </div>
        </div>
        <PublicPageButton
          button={button}
          theme={theme}
          text={t('website_public_page.login.button_text')}
          loading={loading}
          onClick={handleSubmit}
          width="100%"
          paragraph={paragraph}
        />

        {isClient && !isCustomDomain() && (
          <>
            <DividerWithText
              paragraph={paragraph}
              theme={theme}
              text={t('public_page.or_text')}
            />
            <PublicPageGoogleAuthButton
              text={t('public_page.login.google_button_text')}
              button={button}
              width="100%"
            />
          </>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <PublicPageText paragraph={paragraph} theme={theme}>
          {t('website_public_page.login.dont_have_account_text')}
        </PublicPageText>
        <PublicPageLink
          linkButton={linkButton}
          paragraph={paragraph}
          theme={theme}
          href={'/register'}
        >
          {t('website_public_page.login.create_account_button_text')}
        </PublicPageLink>
      </div>
    </div>
  )
}
